import { isNil } from "@clipboard-health/util-ts";

import { isNetworkFailure } from "../loginRum";
import { mapCognitoError } from "./mapCognitoError";
import { isNoAssociatedWorkplacesError } from "./sessionInitErrors";

function extractAxiosCode(error: unknown): string | undefined {
  if (isNil(error) || typeof error !== "object") {
    return undefined;
  }
  const { code } = error as { code?: unknown };
  return typeof code === "string" && code.length > 0 ? code : undefined;
}

function appendCode(message: string, error: unknown): string {
  const code = extractAxiosCode(error);
  return code ? `${message} (code: ${code})` : message;
}

function extractOriginalErrorMessage(error: unknown): string | undefined {
  if (typeof error === "string") {
    const message = error.trim();
    return message.length > 0 ? message : undefined;
  }

  if (isNil(error) || typeof error !== "object") {
    return undefined;
  }

  const { message, name } = error as { message?: unknown; name?: unknown };
  if (typeof message !== "string") {
    return undefined;
  }

  const trimmedMessage = message.trim();
  if (trimmedMessage.length === 0) {
    return undefined;
  }

  const trimmedName = typeof name === "string" ? name.trim() : "";
  if (trimmedName.length > 0 && trimmedName !== "Error") {
    return `${trimmedName}: ${trimmedMessage}`;
  }

  return trimmedMessage;
}

function appendOriginalErrorMessage(message: string, error: unknown): string {
  const originalErrorMessage = extractOriginalErrorMessage(error);
  const messageWithOriginal = originalErrorMessage
    ? `${message} (${originalErrorMessage})`
    : message;

  return appendCode(messageWithOriginal, error);
}

interface FormatSessionInitErrorOptions {
  networkFailureMessage: string;
  noAssociatedWorkplacesMessage?: string;
  defaultMessage: string;
}

export function formatSessionInitErrorMessage(
  error: unknown,
  options: FormatSessionInitErrorOptions
): string {
  if (isNetworkFailure(error)) {
    return appendCode(options.networkFailureMessage, error);
  }

  if (isNoAssociatedWorkplacesError(error) && options.noAssociatedWorkplacesMessage) {
    return options.noAssociatedWorkplacesMessage;
  }

  const mapped = mapCognitoError(error);
  if (mapped.code !== "auth/unknown") {
    return mapped.message;
  }

  return appendOriginalErrorMessage(options.defaultMessage, error);
}
